import { useRef, useState, useEffect, useCallback } from 'react';
import { logger } from '../../utils/logger';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const WS_URL = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const ICE_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]
};

export function useBroadcast() {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamId, setStreamId] = useState(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  
  const localVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const wsRef = useRef(null);
  const peerConnectionsRef = useRef({});

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopStream();
    };
  }, []);

  const getMediaStream = async (sourceType) => {
    try {
      if (sourceType === 'screen') {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true
        });
        try {
          const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          audioStream.getAudioTracks().forEach(track => {
            screenStream.addTrack(track);
          });
        } catch (e) {
          logger.log('No microphone available');
        }
        return screenStream;
      } else {
        return await navigator.mediaDevices.getUserMedia({
          video: videoEnabled,
          audio: audioEnabled
        });
      }
    } catch (error) {
      logger.error('Error getting media:', error);
      toast.error('Failed to access camera/microphone');
      throw error;
    }
  };

  const createOfferForViewer = async (viewerId) => {
    const pc = new RTCPeerConnection(ICE_CONFIG);
    peerConnectionsRef.current[viewerId] = pc;
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }
    
    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current) {
        wsRef.current.send(JSON.stringify({
          type: 'viewer-signal',
          viewer_id: viewerId,
          signal_type: 'ice-candidate',
          data: event.candidate
        }));
      }
    };
    
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      
      if (wsRef.current) {
        wsRef.current.send(JSON.stringify({
          type: 'offer',
          viewer_id: viewerId,
          data: offer
        }));
      }
    } catch (e) {
      logger.error('Error creating offer:', e);
    }
  };

  const handleWebSocketMessage = async (event) => {
    const message = JSON.parse(event.data);
    
    if (message.type === 'viewer-joined') {
      setViewerCount(message.viewer_count);
      await createOfferForViewer(message.viewer_id);
    } else if (message.type === 'viewer-left') {
      setViewerCount(message.viewer_count);
      if (peerConnectionsRef.current[message.viewer_id]) {
        peerConnectionsRef.current[message.viewer_id].close();
        delete peerConnectionsRef.current[message.viewer_id];
      }
    } else if (message.type === 'viewer-answer') {
      const pc = peerConnectionsRef.current[message.viewer_id];
      if (pc && message.data) {
        try {
          await pc.setRemoteDescription(new RTCSessionDescription(message.data));
        } catch (e) {
          logger.error('Error setting remote description:', e);
        }
      }
    } else if (message.type === 'viewer-ice-candidate') {
      const pc = peerConnectionsRef.current[message.viewer_id];
      if (pc && message.data?.candidate) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(message.data));
        } catch (e) {
          logger.error('Error adding ICE candidate:', e);
        }
      }
    }
  };

  const startStream = async (streamTitle, sourceType) => {
    try {
      const stream = await getMediaStream(sourceType);
      localStreamRef.current = stream;
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      const res = await axios.post(`${API_URL}/streams/create?title=${encodeURIComponent(streamTitle)}`);
      const newStreamId = res.data.stream.id;
      setStreamId(newStreamId);
      
      const ws = new WebSocket(`${WS_URL}/ws/broadcast/${newStreamId}`);
      wsRef.current = ws;
      
      ws.onopen = () => {
        logger.log('Broadcaster connected');
        setIsStreaming(true);
        toast.success('Stream started!');
      };
      
      ws.onmessage = handleWebSocketMessage;
      
      ws.onerror = (error) => {
        logger.error('WebSocket error:', error);
        toast.error('Connection error');
      };
      
      ws.onclose = () => {
        logger.log('Broadcaster disconnected');
        setIsStreaming(false);
      };
      
    } catch (error) {
      logger.error('Failed to start stream:', error);
      toast.error('Failed to start stream');
    }
  };

  const stopStream = async () => {
    Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
    peerConnectionsRef.current = {};
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    
    if (streamId) {
      try {
        await axios.post(`${API_URL}/streams/${streamId}/end`);
      } catch (e) {
        logger.error('Error ending stream:', e);
      }
    }
    
    setIsStreaming(false);
    setStreamId(null);
    setViewerCount(0);
    toast.info('Stream ended');
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setVideoEnabled(!videoEnabled);
    }
  };

  const toggleAudio = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setAudioEnabled(!audioEnabled);
    }
  };

  return {
    isStreaming,
    streamId,
    viewerCount,
    videoEnabled,
    audioEnabled,
    localVideoRef,
    localStreamRef,
    startStream,
    stopStream,
    toggleVideo,
    toggleAudio
  };
}
